import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { computeMultiLineRevenue, isMultiLineValuationEnabled } from '../utils/revenueUtils';
import { useCurrency } from '../../../../hooks/useCurrency';

const ProfileStats = ({ user, company }) => {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();
  // Helper function to format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';

    if (num >= 1000000) {
      return `${currencySym}${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${currencySym}${(num / 1000).toFixed(0)}K`;
    }
    return `${currencySym}${num.toLocaleString()}`;
  };

  // Helper function to format number with commas
  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    const num = typeof value === 'string' ? parseInt(value) : value;
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString();
  };

  const stats = [
    {
      value: (() => {
        if (isMultiLineValuationEnabled(company)) {
          const { totals } = computeMultiLineRevenue(user, company);
          return formatCurrency(totals.gross);
        }
        return formatCurrency(user.gross_production || user.annual_revenue);
      })(),
      label: intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.gross_production' }),
      sublabel: '',
    },
    {
      value: (() => {
        if (isMultiLineValuationEnabled(company)) {
          const { totals } = computeMultiLineRevenue(user, company);
          return formatCurrency(totals.recurring);
        }
        return formatCurrency(user.recurring_revenue);
      })(),
      label: intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.recurring' }),
      sublabel: '',
    },
    {
      value: formatNumber(user.number_of_clients),
      label: intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.clients' }),
      sublabel: '',
    },
    {
      value: formatCurrency(user.assets_under_management),
      label: intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.aum_aua' }),
      sublabel: '',
    },
    {
      value: user.years_practicing ? `${user.years_practicing} ${intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.yrs_suffix' })}` : 'N/A',
      label: intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.practicing' }),
      sublabel: '',
    },
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold text-succession-text-primary mb-4">
        {intl.formatMessage({ id: 'succession.profile_drawer.profile_stats.key_metrics' })}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="text-center space-y-1">
            <div className="text-2xl md:text-3xl font-bold text-succession-text-primary">
              {stat.value}
            </div>
            <div className="text-sm text-succession-text-muted font-medium">
              {stat.label}
            </div>
            {stat.sublabel && (
              <div className="text-xs text-succession-text-muted">
                {stat.sublabel}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

ProfileStats.propTypes = {
  user: PropTypes.shape({
    gross_production: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    annual_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    recurring_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    number_of_clients: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    assets_under_management: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    years_practicing: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  company: PropTypes.shape({
    feature_types: PropTypes.arrayOf(PropTypes.string),
    features: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
  }),
};

export default ProfileStats;

